'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      // Evitar chamadas repetidas se o estado não mudar
      if (window.scrollY > 50 && !isScrolled) {
        setIsScrolled(true);
      } else if (window.scrollY <= 50 && isScrolled) {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrolled]);  // Dependência em `isScrolled` para evitar mudanças desnecessárias

  const logoLight = "https://kdpjzumiwajjgvnsvvld.supabase.co/storage/v1/object/public/clientes%20logotipo/logo_telemarcas_paraFundoEscuro.png";
  const logoDark = "https://kdpjzumiwajjgvnsvvld.supabase.co/storage/v1/object/public/clientes%20logotipo/logo_telemarcas2.png";

  useEffect(() => {
    // Verifica o scroll para definir o logo
    setLogoUrl(isScrolled ? logoDark : logoLight);
  }, [isScrolled]); // Reage apenas quando isScrolled mudar

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  const menuItems = [
    { label: 'Início', href: '#inicio' },
    { label: 'Serviços', href: '#servicios' },
    { label: 'Processo', href: '#proceso' },
    { label: 'Blog', href: '#blog' },
    { label: 'Contato', href: '#contacto' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/80 backdrop-blur-xl shadow-sm border-b-2 border-red-500'
          : 'bg-transparent border-b-2 border-transparent'
      }`}
      style={{
        backdropFilter: isScrolled ? 'blur(20px) saturate(180%)' : 'none',
      }}
    >
      <div className="max-w-8xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex-shrink-0 w-24 sm:w-32">
            {logoUrl ? (
              <div className="relative w-32 h-12">
                <Image
                  src={logoUrl}
                  alt="Company Logo"
                  fill
                  className="object-contain"
                  priority
                />
              </div>
            ) : (
              <div
                className={`text-2xl font-black tracking-tighter uppercase transition-colors ${
                  isScrolled ? 'text-foreground' : 'text-white'
                }`}
              >
                Telemarcas
              </div>
            )}
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {menuItems.map((item) => (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.href.substring(1))}
                className={`text-sm font-bold uppercase tracking-wider transition-colors ${
                  isScrolled
                    ? 'text-foreground hover:text-red-600'
                    : 'text-white hover:text-red-400'
                }`}
              >
                {item.label}
              </button>
            ))}
            {/* Adicionando o botão de Login no Menu Desktop */}
            <Link
              href="/login"
              className="bg-red-600 text-white px-6 py-3 text-xs font-black uppercase tracking-wider hover:bg-red-700 transition-all shadow-lg hover:shadow-xl"
            >
              Login
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden transition-colors ${
              isScrolled ? 'text-foreground' : 'text-white'
            }`}
            aria-label="Toggle menu"
          >
            <Icon
              name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={28}
              variant="outline"
            />
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-6 border-b border-red-500/20 w-full">
            <nav className="flex flex-col gap-4 text-sm font-bold uppercase tracking-wider w-full">
              {menuItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => scrollToSection(item.href.substring(1))}
                  className={`text-left transition-colors py-2 w-full ${
                    isScrolled
                      ? 'text-foreground hover:text-red-600'
                      : 'text-white hover:text-red-400'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              {/* Adicionando o botão de Login no Menu Mobile */}
              <Link
                href="/login"
                className="bg-red-600 text-white px-6 py-3 rounded-full text-xs font-black uppercase tracking-wider hover:bg-red-700 transition-all mt-2 w-full"
              >
                Login
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
